<template>
  <div>
    <v-modal short @open="fetchAppProfiles" @close="$emit('close')">
      <template :slot="THREAD_MODAL_HASH.PARTICIPANTS">
        <div
          v-if="isLoading"
          class="d-flex justify-content-center text-muted mt-5 pt-5"
        >
          <v-loader />
        </div>
        <div
          v-else
          :class="[
            'participants__container d-flex flex-column',
            { '--with-profile': withProfile }
          ]"
        >
          <action-header>
            {{ heading }}
          </action-header>
          <action-body>
            <div
              v-if="selfParticipant"
              secondary
              ghost
              class="d-flex align-items-center px-2 py-1 w-100"
            >
              <subscriber-avatar
                small
                :subscriber="selfParticipant.user"
                class="mr-2"
              />
              <div class="d-flex flex-column justify-content-center name">
                {{ $t("components.modals.threadParticipantsModal.youTitle") }}
              </div>
            </div>
            <member-row
              v-for="participant in shownParticipants"
              :key="participant.user.id"
              :member="participant.subscriber"
            >
              <template slot="actions">
                <div
                  v-if="isAdmin && allowToRemove"
                  class="ml-1 remove-button"
                  @click.stop="() => (removingParticipant = participant)"
                >
                  <v-icon icon="cancel" size="2rem"></v-icon>
                </div>
              </template>
            </member-row>
          </action-body>
        </div>

        <infinite-pagination
          list-key="participants"
          :path="`/threads/${messageThreadId}/participants`"
          :init-page="1"
          @loaded="handleLoaded"
        />
      </template>
    </v-modal>

    <alert-dialog
      v-if="removingParticipant"
      :title="
        $t('components.modals.threadParticipantsModal.dialogRemoveConfirm')
      "
      :actions="[
        {
          title: $t(
            'components.modals.threadParticipantsModal.dialogRemoveCancel'
          ),
          callback: () => (removingParticipant = null)
        },
        {
          title: $t(
            'components.modals.threadParticipantsModal.dialogRemoveYes'
          ),
          callback: handleRemoveParticipant
        }
      ]"
      :show="!!removingParticipant"
      @close="() => (removingParticipant = null)"
    >
      {{
        $t("components.modals.threadParticipantsModal.confirmTitle", {
          user_name: removingParticipant.user.name
        })
      }}
    </alert-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from "vuex"

import { THREAD_MODAL_HASH } from "@/lib/messages-utils"
import { isPresent } from "@/lib/utils"

import SubscriberAvatar from "@/components/SubscriberAvatar"
import ActionHeader from "@/components/modals/shared/ActionHeader"
import ActionBody from "@/components/modals/shared/ActionBody"
import MemberRow from "@/components/modals/shared/MemberRow"

import InfinitePagination from "@/components/InfinitePagination"

export default {
  components: {
    SubscriberAvatar,
    ActionHeader,
    ActionBody,
    MemberRow,
    InfinitePagination,
    AlertDialog: () => import("@/components/AlertDialog")
  },

  props: {
    heading: {
      type: String,
      default: function() {
        return this.$t(
          "components.modals.threadParticipantsModal.participantTitle"
        )
      }
    },
    messageThreadId: { type: [Number, String], required: true },
    allowToRemove: { type: Boolean, default: false },
    withProfile: { type: Boolean, default: false }
  },

  data() {
    return {
      isLoading: false,
      participants: [],
      messagingParticipant: null,
      removingParticipant: false,
      initPage: 1,
      THREAD_MODAL_HASH
    }
  },

  computed: {
    ...mapState("messages", ["appMessagingProfiles"]),
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),

    isAdmin() {
      return this.isAuthenticated && this.currentUser?.isAccountAdmin
    },

    selfParticipant() {
      return (
        this.participants.find(
          participant => participant.user.id === this.currentUser?.id
        ) || null
      )
    },

    shownParticipants() {
      return this.participants.filter(
        participant => participant.user.id !== this.currentUser?.id
      )
    }
  },

  methods: {
    ...mapMutations("messages", ["setMessageThread"]),
    ...mapActions("messages", [
      "removeParticipantFromThread",
      "fetchMessagingProfiles"
    ]),

    fetchAppProfiles() {
      if (isPresent(this.appMessagingProfiles)) return

      this.fetchMessagingProfiles()
    },

    handleLoaded({ list }) {
      this.participants = [
        ...new Map(
          [...this.participants, ...list].map(p => [p.user?.id, p])
        ).values()
      ]

      this.setMessageThread({
        id: this.messageThreadId,
        participants: this.participants
      })
    },

    handleRemoveParticipant() {
      const removingUserId = this.removingParticipant.user.id
      if (!removingUserId) return

      this.removeParticipantFromThread({
        messageThreadId: this.messageThreadId,
        userId: removingUserId
      })
        .then(() => {
          this.participants = this.participants.filter(
            participant => participant.user.id != removingUserId
          )

          this.$notify("Successfully Removed")
        })
        .catch(() => {
          this.$notify("An Error Occurred")
        })
        .finally(() => {
          this.removingParticipant = null
        })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.participants__container {
  &.--with-profile {
    cursor: pointer;
  }

  .name {
    font-weight: 600;
  }

  .starting-chat__loader {
    .v-loader {
      width: $size--6;
      height: $size--6;
    }
  }

  .remove-button,
  .message-button {
    @include clickable;

    display: flex;
    align-items: center;

    .v-icon {
      color: $color-ui--grey-60;
    }
  }
}
</style>
